import { ColorEnum } from '@/constants/color'
import { isIOS } from '@/constants/environment'
import Button from '@/features/openTabs/components/AccountActionButton'
import AccountRow, {
    AccountListItem,
} from '@/features/openTabs/components/AccountList/parts/AccountRow'
import Overview from '@/features/openTabs/components/AccountList/parts/Overview'
import StatusTabs, {
    AccountListStatus,
} from '@/features/openTabs/components/AccountList/parts/StatusTabs'
import PlusIcon from '@/features/ui/icons/components/PlusIcon'
import { FC, memo } from 'react'
import { useTranslation } from 'react-i18next'
import { ScrollView, View } from 'react-native'
import { createStyleSheet, useStyles } from 'react-native-unistyles'

export interface AccountListProps {
    accounts: readonly AccountListItem[]
    status: AccountListStatus
    counts: Record<AccountListStatus, number>
    selectedAccountId?: string
    total: string
    onStatusChange: (status: AccountListStatus) => void
    onAccountSelect: (accountId: string) => void
    onCreateAccount: () => void
}

const AccountList: FC<AccountListProps> = ({
    accounts,
    status,
    counts,
    selectedAccountId,
    total,
    onStatusChange,
    onAccountSelect,
    onCreateAccount,
}) => {
    const { t } = useTranslation('app')
    const { styles } = useStyles(stylesheet)

    return (
        <View style={styles.container}>
            <View style={styles.tabs}>
                <StatusTabs value={status} counts={counts} onChange={onStatusChange} />
            </View>
            <View style={styles.listSection}>
                <Overview status={status} count={counts[status]} total={total} />
                <ScrollView
                    contentContainerStyle={styles.listContent}
                    showsVerticalScrollIndicator={false}
                    style={styles.list}
                >
                    {accounts.map((account) => (
                        <AccountRow
                            account={account}
                            status={status}
                            selected={selectedAccountId === account.id}
                            onPress={() => onAccountSelect(account.id)}
                            key={account.id}
                        />
                    ))}
                </ScrollView>
            </View>
            <View style={styles.footer}>
                <Button FrontIcon={PlusIcon} onPress={onCreateAccount} style={styles.createButton}>
                    {t('openTabs.accountList.create')}
                </Button>
            </View>
        </View>
    )
}

const stylesheet = createStyleSheet((_, { insets }) => ({
    container: {
        flex: 1,
        minWidth: 0,
        backgroundColor: ColorEnum.WHITE,
    },
    list: {
        flex: 1,
    },
    tabs: {
        padding: 16,
    },
    listSection: {
        flex: 1,
        gap: 16,
        paddingTop: 16,
        borderTopWidth: 1,
        borderTopColor: ColorEnum.GRAY_200,
    },
    listContent: {
        flexGrow: 1,
    },
    footer: {
        paddingTop: 12,
        paddingHorizontal: 16,
        paddingBottom: isIOS ? Math.max(16, insets.bottom) : 16 + insets.bottom,
        borderTopWidth: 1,
        borderTopColor: ColorEnum.GRAY_200,
        backgroundColor: ColorEnum.WHITE,
    },
    createButton: {
        width: '100%',
        height: 44,
        borderRadius: 8,
        justifyContent: 'center',
    },
}))

export type { AccountListItem, AccountListStatus }
export default memo(AccountList)
